import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Stethoscope, FlaskConical, ClipboardList, LogOut, Bell, Save, RotateCcw, PlugZap, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function useNoindex(title) {
  useEffect(() => {
    document.title = title;
    let m = document.querySelector('meta[name="robots"]');
    const created = !m;
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
    const prev = m.getAttribute("content");
    m.setAttribute("content", "noindex, nofollow");
    return () => { if (created) m.remove(); else if (prev) m.setAttribute("content", prev); };
  }, [title]);
}

const QUICK = [
  ["Property Health", "/admin"], ["Revenue / Transactions", "/admin/ledger"], ["Products / Pricing", "/admin/products"],
  ["Customers", "/admin/customers"], ["Operators", "/admin/operators"], ["Customer Journey", "/owner/test"],
  ["Consultant View", "/operator"], ["Intake Manager", "/owner/intake-manager"], ["SEO Content", "/admin/content"],
  ["Experiments", "/admin/experiments"], ["Reviews", "/admin/reviews"], ["Resource Leads", "/admin/leads"],
];

const STATUS_TONE = {
  CONNECTED: "border-[#667A66]/40 bg-[#667A66]/10 text-[#8bbf8b]",
  "NOT CONNECTED": "border-[#9E4F4F]/30 bg-[#9E4F4F]/10 text-[#d98a8a]",
  "AWAITING DATA": "border-[#C49A5A]/30 bg-[#C49A5A]/10 text-[#d7ac63]",
};

export default function OwnerHome() {
  const nav = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [sources, setSources] = useState(null);
  const [resetting, setResetting] = useState(false);
  useNoindex("Owner Control Center");
  useEffect(() => {
    http.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    http.get("/admin/data-sources").then((r) => setSources(r.data.sources)).catch(() => {});
  }, []);

  const resetTestCustomer = async () => {
    if (!window.confirm("Reset the test customer to a clean slate? This clears all test journey data.")) return;
    setResetting(true);
    try { await http.post("/owner/test/reset"); toast.success("Test customer reset"); }
    catch (e) { toast.error("Reset failed"); } finally { setResetting(false); }
  };

  const saveSettings = async (next) => {
    setSettings(next);
    try { await http.put("/admin/settings", next); toast.success("Settings saved"); } catch (e) { toast.error("Could not save"); }
  };

  const viewAsCustomer = async () => {
    try {
      await http.post("/owner/impersonate");
      window.location.assign("/portal");
    } catch (e) { toast.error("Could not enter customer test mode."); }
  };

  const MAIN = [
    ["View as Customer", "See the portal as a parent — Clarity, Blueprint, Guided & In-Home.", User, viewAsCustomer, "owner-view-customer"],
    ["View as Certified Infant Sleep Consultant", "See the consultant queues, review workspace & Blueprint builder.", Stethoscope, () => nav("/operator"), "owner-view-consultant"],
    ["Test Full Journey", "Create/reset a safe test customer and simulate every stage.", FlaskConical, () => nav("/owner/test"), "owner-test-journey"],
    ["Intake & Workflow Manager", "Edit, reorder & publish every intake question and preview the full workflow.", ClipboardList, () => nav("/owner/intake-manager"), "owner-intake-manager"],
  ];

  return (
    <div className="min-h-screen bg-[#161514] text-[#F7F5F0]">
      <div className="max-w-4xl mx-auto px-5 py-10 sm:py-14">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="eyebrow text-[#C2836B] mb-1">Owner Control Center</div>
            <h1 className="font-display text-3xl sm:text-4xl">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
          </div>
          <button data-testid="owner-signout" onClick={() => { logout(); nav("/login"); }}
            className="flex items-center gap-1.5 text-sm text-[#A39E98] hover:text-[#F7F5F0] transition-colors">
            <LogOut size={15} /> Sign out
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4" data-testid="owner-main-actions">
          {MAIN.map(([title, sub, Icon, onClick, tid]) => (
            <button key={title} data-testid={tid} onClick={onClick}
              className="card-night rounded-2xl p-6 text-left hover:border-[#C2836B]/60 transition-colors group">
              <div className="w-12 h-12 rounded-xl bg-[#C2836B]/15 grid place-items-center mb-4">
                <Icon size={22} className="text-[#C2836B]" />
              </div>
              <h2 className="font-display text-xl">{title}</h2>
              <p className="text-[#A39E98] text-sm mt-1.5 leading-relaxed">{sub}</p>
            </button>
          ))}
        </div>

        <div className="mt-10">
          <div className="eyebrow text-[#738075] mb-3 flex items-center gap-2"><PlugZap size={13} /> Data source status</div>
          {sources ? (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="owner-data-sources">
              {sources.map((s) => {
                const Icon = s.status === "CONNECTED" ? CheckCircle2 : s.status === "AWAITING DATA" ? Clock : PlugZap;
                return (
                  <div key={s.key} data-testid={`source-${s.key}`} className="card-night rounded-xl p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[#F7F5F0] text-sm font-medium">{s.label}</div>
                      <div className="text-[#A39E98] text-xs mt-0.5">{s.detail}</div>
                    </div>
                    <span className={`shrink-0 text-[10px] uppercase tracking-wide rounded-full border px-2.5 py-1 flex items-center gap-1 ${STATUS_TONE[s.status]}`}>
                      <Icon size={11} /> {s.status}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-[#6b665e] text-sm">Loading…</p>}
          <p className="text-[11px] text-[#6b665e] mt-3 leading-relaxed">Numbers that depend on NOT CONNECTED sources are shown as NOT CONNECTED or AWAITING DATA across the dashboard — never faked.</p>
        </div>

        <div className="mt-10">
          <div className="eyebrow text-[#738075] mb-3 flex items-center gap-2"><FlaskConical size={13} /> Test customer</div>
          <div className="card-night rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-[#A39E98]">One reusable test customer powers every preview. Reset it to a clean slate anytime — real customer data is never touched.</p>
            <div className="flex gap-2 shrink-0">
              <button data-testid="owner-test-journey-link" onClick={() => nav("/owner/test")}
                className="text-sm bg-[#C2836B]/15 border border-[#C2836B]/40 rounded-full px-4 py-2 text-[#C2836B] hover:bg-[#C2836B]/25 transition-colors">Open Test Journey</button>
              <button data-testid="owner-reset-test-customer" onClick={resetTestCustomer} disabled={resetting}
                className="flex items-center gap-1.5 text-sm border border-[#3a3835] rounded-full px-4 py-2 text-[#A39E98] hover:text-[#F7F5F0] hover:border-[#C2836B]/50 transition-colors disabled:opacity-50">
                <RotateCcw size={14} /> {resetting ? "Resetting…" : "Reset Test Customer"}</button>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="eyebrow text-[#738075] mb-3 flex items-center gap-2"><Bell size={13} /> Notifications & reminders</div>
          {settings ? (
            <div className="card-night rounded-2xl p-6 space-y-4" data-testid="owner-settings">
              <Toggle label="Guided message email alerts — parent" value={settings.guided_parent_alerts} onChange={(v) => saveSettings({ ...settings, guided_parent_alerts: v })} tid="set-parent-alerts" />
              <Toggle label="Guided message email alerts — consultant" value={settings.guided_consultant_alerts} onChange={(v) => saveSettings({ ...settings, guided_consultant_alerts: v })} tid="set-consultant-alerts" />
              <Toggle label="In-Home + Overnight visit reminder" value={settings.reminder_enabled} onChange={(v) => saveSettings({ ...settings, reminder_enabled: v })} tid="set-reminder-enabled" />
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-[#A39E98]">Reminder timing (hours before visit)</span>
                <div className="flex items-center gap-2">
                  <input type="number" min="1" max="168" data-testid="set-reminder-hours" value={settings.reminder_hours}
                    onChange={(e) => setSettings((s) => ({ ...s, reminder_hours: e.target.value }))}
                    className="w-20 bg-[#1C1B1A] border border-[#2E2C2A] rounded-lg px-3 py-1.5 text-[#F7F5F0] text-sm" />
                  <button data-testid="set-reminder-save" onClick={() => saveSettings({ ...settings, reminder_hours: Number(settings.reminder_hours) })}
                    className="flex items-center gap-1.5 text-sm bg-[#C2836B]/15 border border-[#C2836B]/40 rounded-full px-3 py-1.5 text-[#C2836B] hover:bg-[#C2836B]/25 transition-colors"><Save size={13} /> Save</button>
                </div>
              </div>
            </div>
          ) : <p className="text-[#6b665e] text-sm">Loading settings…</p>}
        </div>

        <div className="mt-10">
          <div className="eyebrow text-[#738075] mb-3 flex items-center gap-2"><Bell size={13} /> Property contact (shown sitewide & in portal)</div>
          {settings ? (
            <div className="card-night rounded-2xl p-6 space-y-4" data-testid="owner-contact-settings">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <span className="text-sm text-[#A39E98]">Support email</span>
                <input data-testid="set-support-email" value={settings.support_email || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, support_email: e.target.value }))}
                  className="w-full sm:w-80 bg-[#1C1B1A] border border-[#2E2C2A] rounded-lg px-3 py-1.5 text-[#F7F5F0] text-sm" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <span className="text-sm text-[#A39E98]">Support phone</span>
                <input data-testid="set-support-phone" value={settings.support_phone || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, support_phone: e.target.value }))}
                  className="w-full sm:w-80 bg-[#1C1B1A] border border-[#2E2C2A] rounded-lg px-3 py-1.5 text-[#F7F5F0] text-sm" />
              </div>
              <div className="flex justify-end">
                <button data-testid="set-contact-save" onClick={() => saveSettings({ ...settings, support_email: (settings.support_email || "").trim(), support_phone: (settings.support_phone || "").trim() })}
                  className="flex items-center gap-1.5 text-sm bg-[#C2836B]/15 border border-[#C2836B]/40 rounded-full px-3 py-1.5 text-[#C2836B] hover:bg-[#C2836B]/25 transition-colors"><Save size={13} /> Save contact</button>
              </div>
              <p className="text-[11px] text-[#6b665e] leading-relaxed">These power the footer, Contact page, and portal "Need Help?" area. Changes apply after your next visit/refresh.</p>
            </div>
          ) : null}
        </div>

        <div className="mt-10">
          <div className="eyebrow text-[#738075] mb-3">Quick links</div>
          <div className="flex flex-wrap gap-2">
            {QUICK.map(([label, to]) => (
              <button key={label} onClick={() => nav(to)}
                className="text-sm bg-[#1C1B1A] border border-[#2E2C2A] rounded-full px-4 py-2 text-[#A39E98] hover:text-[#F7F5F0] hover:border-[#C2836B]/50 transition-colors">
                {label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-[#6b665e] text-xs mt-10 leading-relaxed">
          Private owner area · not linked publicly · noindex. Test actions use a dedicated test customer and never touch real customer records, revenue, or reports.
        </p>
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange, tid }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-[#A39E98]">{label}</span>
      <button data-testid={tid} onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${value ? "bg-[#C2836B]" : "bg-[#2E2C2A]"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}
